import SiteConfig from '@/services/SiteConfig'

/**
 * Site-derived constant tables, built at access time instead of freezing the
 * import-time site: SiteConfig resolves per request on the server, and these
 * modules load lazily with whichever request touches them first (VIP-12037).
 * One table is cached per site; the proxy keeps the plain property syntax
 * consumers already use.
 *
 * @template {object} T
 * @param {(site: typeof SiteConfig) => T} build
 * @returns {T}
 */
// Our own host set is small (three shops plus staging/local variants). The cap
// is a backstop against an unexpected host stream growing this map forever in
// a long-lived server process: past it, tables are still correct, just rebuilt
// per access rather than cached.
const MAX_CACHED_SITES = 16

export const siteDerived = (build) => {
  const cache = new Map()
  const current = () => {
    const key = `${SiteConfig.apiRoot}|${SiteConfig.pageRoot}|${SiteConfig.domain}`
    let table = cache.get(key)
    if (!table) {
      table = build(SiteConfig)
      if (cache.size < MAX_CACHED_SITES) cache.set(key, table)
    }
    return table
  }

  return new Proxy(
    {},
    {
      get: (_, prop) => current()[prop],
      has: (_, prop) => prop in current(),
      ownKeys: () => Reflect.ownKeys(current()),
      getOwnPropertyDescriptor: (_, prop) =>
        Object.getOwnPropertyDescriptor(current(), prop)
    }
  )
}
